import React, { useEffect, useRef, useState } from 'react';

const REDIRECT_SECONDS = 4;

declare global {
    interface Window {
        gtag?: (...args: unknown[]) => void;
    }
}

/** Destination on blanche.vc, preserving query params (UTM etc.) and hash. */
const destinationUrl = () =>
    `https://blanche.vc/${window.location.search}${window.location.hash}`;

/**
 * Blocking rebrand interstitial: v3nture.studio has become blanche.vc.
 * Non-dismissible, locks scroll, counts down and redirects.
 * Remove once the domain is flipped to a hard 301 (see SEO-STRATEGY.md).
 */
const RebrandRedirect: React.FC = () => {
    const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);
    const ctaRef = useRef<HTMLAnchorElement>(null);

    useEffect(() => {
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        ctaRef.current?.focus({ preventScroll: true });

        window.gtag?.('event', 'rebrand_interstitial_shown', {
            destination: 'https://blanche.vc/',
            transport_type: 'beacon',
        });

        const tick = setInterval(
            () => setSecondsLeft((s) => Math.max(0, s - 1)),
            1000
        );
        return () => clearInterval(tick);
    }, []);

    useEffect(() => {
        if (secondsLeft > 0) return;
        // replace() keeps v3nture.studio out of the back-button loop
        window.location.replace(destinationUrl());
    }, [secondsLeft]);

    const handleManualClick = () => {
        window.gtag?.('event', 'rebrand_redirect_click', {
            destination: 'https://blanche.vc/',
            transport_type: 'beacon',
        });
    };

    return (
        <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="rebrand-title"
            aria-describedby="rebrand-desc"
            className="fixed inset-0 z-[100000] flex items-center justify-center overflow-hidden bg-black px-4"
        >
            <style>{`
                @keyframes rebrand-progress {
                    from { transform: scaleX(0); }
                    to { transform: scaleX(1); }
                }
                @keyframes rebrand-card-in {
                    from { opacity: 0; transform: translateY(12px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @media (prefers-reduced-motion: reduce) {
                    .rebrand-card { animation: none !important; }
                }
            `}</style>

            {/* Ambient backdrop */}
            <div
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-radial from-white/[0.06] via-transparent to-transparent"
            />

            <div
                className="rebrand-card relative w-full max-w-lg rounded-lg border border-white/20 bg-black/90 p-8 text-center shadow-2xl shadow-white/10 sm:p-10"
                style={{ animation: 'rebrand-card-in 0.5s ease-out both' }}
            >
                <h1
                    id="rebrand-title"
                    className="mb-4 text-2xl font-light tracking-tight text-white sm:text-3xl"
                >
                    v3nture.studio is now{' '}
                    <span className="font-medium">blanche.vc</span>
                </h1>

                <p id="rebrand-desc" className="mb-8 font-light text-white/70">
                    Same team, same portfolio — one brand. Our venture studio
                    now lives at blanche.vc.
                </p>

                <a
                    ref={ctaRef}
                    href="https://blanche.vc/"
                    onClick={handleManualClick}
                    className="group relative block w-full overflow-hidden border border-white/40 py-3 font-light tracking-wide text-white transition-all duration-300 hover:bg-white hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                >
                    <span className="relative z-10">
                        Take me there now&nbsp;→
                    </span>
                </a>

                <p
                    className="mt-6 text-sm font-light text-white/50"
                    aria-live="polite"
                >
                    Redirecting in{' '}
                    <span className="font-mono text-white/80">
                        {secondsLeft}
                    </span>
                    s
                </p>

                <div
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-px overflow-hidden rounded-b-lg"
                >
                    <div
                        className="h-full w-full origin-left bg-white/60"
                        style={{
                            animation: `rebrand-progress ${REDIRECT_SECONDS}s linear forwards`,
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default RebrandRedirect;
